import { useLayoutEffect, useRef, useState } from 'react'
import { useScroll, useTransform } from 'motion/react'

/** The block is at least a viewport tall: pin it at the top, or by its bottom edge when taller. */
export const getStickyTop = (viewportHeight: number, stickyHeight: number) =>
  Math.min(0, viewportHeight - stickyHeight)

export const useScrollDrivenTrack = () => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [distance, setDistance] = useState(0)
  const [stickyHeight, setStickyHeight] = useState(0)
  const [stickyTop, setStickyTop] = useState(0)

  useLayoutEffect(() => {
    const track = trackRef.current
    const sticky = stickyRef.current
    if (!track || !sticky) return undefined

    const measure = () => {
      setDistance(Math.max(0, track.scrollWidth - track.clientWidth))
      setStickyHeight(sticky.offsetHeight)
      setStickyTop(getStickyTop(window.innerHeight, sticky.offsetHeight))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(track)
    observer.observe(sticky)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  // Runs while the block is pinned at `stickyTop`, exactly `distance` px of scroll
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: [`start ${stickyTop}px`, `end ${stickyTop + stickyHeight}px`],
  })
  const x = useTransform(scrollYProgress, [0, 1], [0, -distance])

  return {
    sectionRef,
    stickyRef,
    trackRef,
    sectionHeight: stickyHeight + distance,
    stickyTop,
    x,
  }
}
